import { PLAYER_CARD_BASE_HEIGHT } from "@/design-system/PlayerCard/PlayerCardView";
import { useInFlowHeight } from "@/hooks/useInFlowHeight";
import { cn } from "@/lib/utils";
import {
  CurrentStepData,
  EvolutionBuilderPlayer,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import {
  cleanTouchBarMenuZIndex,
  setTouchBarMenuZIndex,
  topToPageSelector,
} from "@/redux/globalState/globalStateSlice";
import { AppDispatch } from "@/redux/store";
import type { GroupedUpgrades } from "@repo/shared/evolutions";
import { ReactNode, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  HEADER_CARD_SCALE,
  PlayerAndUpgradeSection,
} from "./components/PlayerAndUpgradeSection";

export type CardBuilderVariant = "evo-builder" | "playstyles-lab";

interface CardBuilderTopSectionProps {
  basePlayer: EvolutionBuilderPlayer;
  currentEvolutionPlayer: EvolutionBuilderPlayer;
  isEvolutionPlayer: boolean;
  scrollPastTopBar: boolean;
  handlePlayerClicked: () => void;
  loading?: boolean;
  currentStepData: CurrentStepData | null;
  upgrades: GroupedUpgrades;
  handleClearPlaystyles?: () => void;
  variant?: CardBuilderVariant;
  onRemovePlayStyle?: (id: number, plus: boolean) => void;
  removeDisabled?: boolean;
  /**
   * Rendered inside the pinned bar, under the player, so the bar and the
   * chain read as one surface. Ignored while the header is in the flow.
   */
  stickyChain?: ReactNode;
  /**
   * The pinned bar's height without the chain row, so the page can pin the
   * chain the moment it slides under the bar rather than at a guessed offset.
   */
  onPinnedHeightChange?: (height: number) => void;
}

const FLOW_CONTAINER =
  "relative mx-auto flex w-full max-w-content px-2 large:px-16 touch-device:px-4";
/*
  Tucks the header up under the hero title on desktop. Not below the
  touch-device breakpoint: there is no hero title above it there, so the
  margin would collapse through <main> and pull the page up under the top
  nav. (`touch-device:` is a min-width variant here; `max-touch-device:`
  is the phones-and-tablets side, matching BREAKPOINTS.TOUCH_DEVICE.)
*/
const FLOW_MARGIN = "-mt-4 max-touch-device:mt-0";

/**
 * Stands in for the header until the player has loaded, at the height the
 * header will have: the card is its tallest part, so the card's box is the
 * reservation. Without it the toolbar and steps jump down on load.
 */
export const CardBuilderTopSectionSkeleton = () => (
  <div className={cn(FLOW_CONTAINER, FLOW_MARGIN)} aria-hidden>
    <div className="flex w-full items-center gap-3 touch-device:gap-5">
      <div
        className="shrink-0 animate-pulse rounded-xl bg-surface-raised/40"
        style={{
          width: 180 * HEADER_CARD_SCALE,
          height: PLAYER_CARD_BASE_HEIGHT * HEADER_CARD_SCALE,
        }}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-7 w-40 animate-pulse rounded-full bg-surface-raised/40" />
        <div className="h-24 w-full animate-pulse rounded-xl bg-background/70" />
      </div>
    </div>
  </div>
);

/**
 * The player header in the hero. Once the page scrolls past it, a condensed
 * copy pins under the top bar so the player stays in view while browsing
 * steps.
 */
export const CardBuilderTopSection = ({
  basePlayer,
  currentEvolutionPlayer,
  isEvolutionPlayer,
  scrollPastTopBar,
  handlePlayerClicked,
  loading,
  currentStepData,
  upgrades,
  handleClearPlaystyles,
  variant = "evo-builder",
  onRemovePlayStyle,
  removeDisabled,
  stickyChain,
  onPinnedHeightChange,
}: CardBuilderTopSectionProps) => {
  const topToPage = useSelector(topToPageSelector);
  const dispatch = useDispatch<AppDispatch>();
  // The spacer left behind while pinned must match the in-flow height
  // exactly, or the steps below jump the moment the bar pins.
  const [flowRef, flowHeight] = useInFlowHeight(!scrollPastTopBar);
  const barRef = useRef<HTMLDivElement>(null);
  const chainRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollPastTopBar || !onPinnedHeightChange) return;
    const bar = barRef.current;
    if (!bar) return;
    const report = () =>
      onPinnedHeightChange(
        bar.getBoundingClientRect().height -
          (chainRowRef.current?.getBoundingClientRect().height ?? 0),
      );
    report();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(report);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [scrollPastTopBar, onPinnedHeightChange]);

  useEffect(() => {
    dispatch(setTouchBarMenuZIndex("z-10"));
    return () => {
      dispatch(cleanTouchBarMenuZIndex());
    };
  }, [scrollPastTopBar]);

  return (
    <>
      {/* The spacer carries the same negative margin as the in-flow header so
          swapping them moves nothing. */}
      {scrollPastTopBar && (
        <div className={FLOW_MARGIN} style={{ height: flowHeight ?? 0 }} />
      )}
      <div
        ref={flowRef}
        className={cn(
          FLOW_CONTAINER,
          !scrollPastTopBar && FLOW_MARGIN,
          scrollPastTopBar && topToPage.tailwindString,
          scrollPastTopBar &&
            "fixed left-1/2 z-10 -translate-x-1/2 px-0 large:px-0 touch-device:px-0",
        )}
      >
        {/* No card around it: the header sits straight on the hero. Pinned,
            it becomes a bar on the page background so the steps scroll
            under something opaque. The chain, once reached, joins the same
            bar below a divider rather than pinning as a second card. */}
        <div
          ref={barRef}
          className={cn(
            "flex w-full flex-col",
            scrollPastTopBar &&
              "bg-background/95 px-3 py-2 shadow-navigation-bar backdrop-blur-xl touch-device:px-4",
          )}
        >
          <PlayerAndUpgradeSection
            basePlayer={basePlayer}
            currentEvolutionPlayer={currentEvolutionPlayer}
            isEvolutionPlayer={isEvolutionPlayer}
            handlePlayerClicked={handlePlayerClicked}
            loading={loading}
            condensed={scrollPastTopBar}
            currentStepData={currentStepData}
            upgrades={upgrades}
            handleClearPlaystyles={handleClearPlaystyles}
            variant={variant}
            onRemovePlayStyle={onRemovePlayStyle}
            removeDisabled={removeDisabled}
          />
          {scrollPastTopBar && stickyChain && (
            <div ref={chainRowRef} className="pt-2">
              <div className="border-t border-edge-strong pt-2">
                {stickyChain}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
